// ── InvestmentPartners.jsx ────────────────────────────────────────────────────
import { useEffect, useState, useCallback } from "react";
import { investAPI } from "../services/api";
import { RefreshCw, TrendingUp, DollarSign, Users, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";
import { format } from "date-fns";

const PKG_COLOR = {
  silver:  { badge: "badge-offline",  dot: "#94a3b8", label: "Silver"  },
  gold:    { badge: "badge-pending",  dot: "#f5a623", label: "Gold"    },
  premium: { badge: "badge-payment",  dot: "#a855f7", label: "Premium" },
};

const STATUS_COLOR = {
  pending: "badge-pending",
  paid:    "badge-paid",
  failed:  "badge-cancelled",
};

export default function InvestmentPartners() {
  const [rows,    setRows]    = useState([]);
  const [summary, setSummary] = useState(null);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);

  // Filters
  const [statusFilter,  setStatusFilter]  = useState("");
  const [packageFilter, setPackageFilter] = useState("");
  const [search,        setSearch]        = useState("");

  // Detail modal
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await investAPI.list({ page, limit: 15, status: statusFilter, package: packageFilter });
      setRows(r.data.data.data || []);
      setSummary(r.data.data.summary || null);
      setTotal(r.data.data.meta?.total || 0);
    } catch {
      toast.error("Failed to load investment partners");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, packageFilter]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.ceil(total / 15);

  // Client-side search filter on top of server pagination
  const visible = search.trim()
    ? rows.filter(r =>
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.email.toLowerCase().includes(search.toLowerCase()) ||
        r.phone.includes(search) ||
        r.reference.toLowerCase().includes(search.toLowerCase())
      )
    : rows;

  return (
    <div>
      <style>{`
        .invest-mobile-cards  { display: none; }
        .invest-desktop-table { display: block; }
        .invest-card {
          padding: 14px 16px;
          border-bottom: 1px solid var(--border);
          cursor: pointer;
          transition: background 0.15s;
        }
        .invest-card:last-child { border-bottom: none; }
        .invest-card:hover { background: var(--bg3); }
        @media (max-width: 768px) {
          .invest-mobile-cards  { display: block; }
          .invest-desktop-table { display: none; }
        }
      `}</style>

      {/* ── Page header ── */}
      <div className="page-header">
        <div>
          <div className="page-title">💰 Investment Partners</div>
          <div className="page-subtitle">Fleet investment registrations & payment status</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}>
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      {/* ── Summary stat cards ── */}
      {summary && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          <div className="stat-card accent">
            <div className="stat-label">Total Registrations</div>
            <div className="stat-value">{summary.total_registrations || 0}</div>
            <div className="stat-sub">All packages</div>
            <Users size={40} className="stat-icon"/>
          </div>
          <div className="stat-card green">
            <div className="stat-label">Confirmed Payments</div>
            <div className="stat-value">{summary.total_paid || 0}</div>
            <div className="stat-sub">Status: paid</div>
            <CheckCircle size={40} className="stat-icon"/>
          </div>
          <div className="stat-card blue">
            <div className="stat-label">Total Raised (GHS)</div>
            <div className="stat-value">
              {parseFloat(summary.total_raised || 0).toLocaleString("en-GH", { maximumFractionDigits: 0 })}
            </div>
            <div className="stat-sub">Paid investments only</div>
            <DollarSign size={40} className="stat-icon"/>
          </div>
          <div className="stat-card purple">
            <div className="stat-label">Monthly Obligations</div>
            <div className="stat-value">
              GHS {(summary.total_paid
                ? rows.filter(r => r.status === "paid").reduce((acc, r) => {
                    const mo = { silver: 500, gold: 1000, premium: 1500 };
                    return acc + (mo[r.package] || 0);
                  }, 0)
                : 0
              ).toLocaleString()}
            </div>
            <div className="stat-sub">Monthly returns due</div>
            <TrendingUp size={40} className="stat-icon"/>
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="filters-bar">
        <input
          className="input search-input"
          placeholder="Search name, email, phone, ref…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="failed">Failed</option>
        </select>
        <select className="select" value={packageFilter} onChange={e => { setPackageFilter(e.target.value); setPage(1); }}>
          <option value="">All packages</option>
          <option value="silver">Silver</option>
          <option value="gold">Gold</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      {/* ── Table / cards ── */}
      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner"/></div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="invest-mobile-cards">
              {visible.length === 0 ? (
                <div className="empty-state"><p>No investment partners found</p></div>
              ) : visible.map(row => (
                <div key={row.id} className="invest-card" onClick={() => setSelected(row)}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14 }}>{row.name}</div>
                      <div style={{ fontSize:12, color:"var(--text3)" }}>{row.email}</div>
                    </div>
                    <span className={`badge ${STATUS_COLOR[row.status]}`}>{row.status}</span>
                  </div>
                  <div style={{ display:"flex", gap:10, fontSize:12, color:"var(--text3)", flexWrap:"wrap", alignItems:"center" }}>
                    <span className={`badge ${PKG_COLOR[row.package]?.badge}`}>{row.package}</span>
                    <span style={{ fontFamily:"var(--font-mono)" }}>GHS {Number(row.amount).toLocaleString()}</span>
                    <span>{row.phone}</span>
                    {row.paid_at && <span>Paid {format(new Date(row.paid_at), "MMM d")}</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="invest-desktop-table table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Partner</th>
                    <th>Phone</th>
                    <th>Package</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Reference</th>
                    <th>Registered</th>
                    <th>Paid At</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <tr><td colSpan={9}><div className="empty-state"><p>No investment partners found</p></div></td></tr>
                  ) : visible.map(row => (
                    <tr
                      key={row.id}
                      style={{ cursor:"pointer" }}
                      onClick={() => setSelected(row)}
                      onMouseOver={e => e.currentTarget.style.background = "var(--bg3)"}
                      onMouseOut={e  => e.currentTarget.style.background = ""}
                    >
                      <td style={{ color:"var(--text3)", fontSize:12 }}>#{row.id}</td>
                      <td>
                        <div className="fw-bold">{row.name}</div>
                        <div style={{ fontSize:11, color:"var(--text3)" }}>{row.email}</div>
                      </td>
                      <td style={{ fontFamily:"var(--font-mono)", fontSize:12, color:"var(--text2)" }}>{row.phone}</td>
                      <td>
                        <span className={`badge ${PKG_COLOR[row.package]?.badge}`}>
                          {row.package}
                        </span>
                      </td>
                      <td style={{ fontFamily:"var(--font-mono)", fontWeight:700 }}>
                        GHS {Number(row.amount).toLocaleString()}
                      </td>
                      <td>
                        <span className={`badge ${STATUS_COLOR[row.status]}`}>{row.status}</span>
                      </td>
                      <td>
                        <span style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text3)" }}>
                          {row.reference}
                        </span>
                      </td>
                      <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text3)" }}>
                        {row.created_at ? format(new Date(row.created_at), "MMM d, yyyy") : "—"}
                      </td>
                      <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--green)" }}>
                        {row.paid_at ? format(new Date(row.paid_at), "MMM d, HH:mm") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <span>Showing {visible.length} of {total}</span>
              <div className="pagination-btns">
                <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <span style={{ padding:"0 8px", color:"var(--text2)" }}>Page {page} of {pages || 1}</span>
                <button className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Detail modal ── */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Partner Details</div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="modal-body">

              {/* Status banner */}
              <div style={{
                display:"flex", alignItems:"center", justifyContent:"space-between",
                background: selected.status === "paid"
                  ? "rgba(34,197,94,0.08)"
                  : selected.status === "failed"
                    ? "rgba(239,68,68,0.08)"
                    : "rgba(245,166,35,0.08)",
                border: `1px solid ${selected.status === "paid" ? "rgba(34,197,94,0.2)" : selected.status === "failed" ? "rgba(239,68,68,0.2)" : "rgba(245,166,35,0.2)"}`,
                borderRadius: 10, padding:"12px 16px", marginBottom:20,
              }}>
                <div>
                  <div style={{ fontSize:12, color:"var(--text3)", marginBottom:3 }}>Payment Status</div>
                  <span className={`badge ${STATUS_COLOR[selected.status]}`} style={{ fontSize:13 }}>
                    {selected.status === "paid" ? "✅ " : selected.status === "failed" ? "❌ " : "⏳ "}
                    {selected.status.toUpperCase()}
                  </span>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:12, color:"var(--text3)", marginBottom:3 }}>Package</div>
                  <span className={`badge ${PKG_COLOR[selected.package]?.badge}`} style={{ fontSize:13 }}>
                    {selected.package === "premium" ? "💎" : selected.package === "gold" ? "🥇" : "🥈"} {selected.package}
                  </span>
                </div>
              </div>

              {/* Partner info */}
              {[
                { label:"Full Name",    value: selected.name },
                { label:"Email",        value: selected.email },
                { label:"Phone",        value: selected.phone },
                { label:"Amount",       value: `GHS ${Number(selected.amount).toLocaleString()}` },
                { label:"Monthly Return", value: `GHS ${({ silver:500, gold:1000, premium:1500 }[selected.package] || "—").toLocaleString()}/month` },
                { label:"Reference",    value: selected.reference, mono:true },
                { label:"Paystack Ref", value: selected.paystack_ref || "—", mono:true },
                { label:"Registered",   value: selected.created_at ? format(new Date(selected.created_at), "MMM d, yyyy HH:mm") : "—" },
                { label:"Paid At",      value: selected.paid_at ? format(new Date(selected.paid_at), "MMM d, yyyy HH:mm") : "Not yet paid" },
              ].map(row => (
                <div key={row.label} style={{
                  display:"flex", justifyContent:"space-between", alignItems:"flex-start",
                  padding:"10px 0", borderBottom:"1px solid var(--border)",
                }}>
                  <span style={{ fontSize:12, color:"var(--text3)", minWidth:120 }}>{row.label}</span>
                  <span style={{
                    fontSize: row.mono ? 11 : 13,
                    fontWeight: row.mono ? 400 : 600,
                    fontFamily: row.mono ? "'DM Mono', monospace" : "inherit",
                    color: row.label === "Paid At" && selected.paid_at ? "var(--green)" : "var(--text)",
                    textAlign:"right", wordBreak:"break-all", maxWidth:"60%",
                  }}>
                    {row.value}
                  </span>
                </div>
              ))}

              {/* WhatsApp CTA */}
              <a
                href={`https://wa.me/${selected.phone.replace(/\D/g,"").replace(/^0/, "233")}?text=Hi ${encodeURIComponent(selected.name)}, this is Kwikie Express regarding your ${selected.package} investment package.`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                  marginTop:20, padding:"11px", borderRadius:10,
                  background:"rgba(37,211,102,0.1)", border:"1px solid rgba(37,211,102,0.25)",
                  color:"#25D366", fontWeight:700, fontSize:13, textDecoration:"none",
                  transition:"background 0.15s",
                }}
                onMouseOver={e => e.currentTarget.style.background = "rgba(37,211,102,0.18)"}
                onMouseOut={e  => e.currentTarget.style.background = "rgba(37,211,102,0.1)"}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp {selected.name.split(" ")[0]}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}